import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Languages } from 'lucide-react'
import { Button } from 'design-system/components/ui/button'
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
 DropdownMenuTrigger,
} from 'design-system/components/ui/dropdown-menu'
import { cn } from 'design-system/lib/utils'
import { getCurrentAppLanguage, type AppLanguage } from 'helpers/locale'

export const APP_LANGUAGE_OPTIONS: { code: AppLanguage; label: string; hint: string }[] = [
 { code: 'ar', label: 'العربية', hint: 'Arabic' },
 { code: 'en', label: 'English', hint: 'English' },
 { code: 'fr', label: 'Français', hint: 'French' },
]

const LANGUAGE_OPTIONS = APP_LANGUAGE_OPTIONS

export default function LanguageSwitcher({ className }: { className?: string }) {
 const { i18n, t } = useTranslation()
 const current = getCurrentAppLanguage()
 const menuDir = i18n.dir()

 const setLanguage = (code: AppLanguage) => {
  if (code !== current) {
   void i18n.changeLanguage(code)
  }
 }

 return (
  <DropdownMenu>
   <DropdownMenuTrigger asChild>
    <Button
     type="button"
     variant="ghost"
     size="icon-sm"
     className={cn('shrink-0', className)}
     title={t('Language')}
     aria-label={t('Language')}
    >
     <Languages className="h-4 w-4" />
    </Button>
   </DropdownMenuTrigger>
   <DropdownMenuContent
    align="end"
    sideOffset={8}
    dir={menuDir}
    className="min-w-[11.5rem] overflow-hidden rounded-xl border border-border-subtle bg-popover p-1.5 text-popover-foreground shadow-lg"
   >
    <DropdownMenuLabel className="px-2.5 py-1.5 text-xs font-semibold text-muted-foreground">
     {t('Language')}
    </DropdownMenuLabel>
    <DropdownMenuSeparator className="my-1 bg-border-subtle/80" />
    {LANGUAGE_OPTIONS.map((option) => {
     const selected = current === option.code
     return (
      <DropdownMenuItem
       key={option.code}
       className={cn(
        'cursor-pointer rounded-lg px-2.5 py-2 focus:bg-muted/80',
        selected && 'bg-primary/[0.06] focus:bg-primary/[0.08]',
       )}
       onClick={() => setLanguage(option.code)}
      >
       <div className="flex w-full items-center gap-3">
        <span
         className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-[11px] font-bold uppercase tabular-nums',
          selected
           ? 'border-primary/25 bg-primary/10 text-primary'
           : 'border-border-subtle/80 bg-muted/50 text-muted-foreground',
         )}
         aria-hidden
        >
         {option.code}
        </span>
        <span className="min-w-0 flex-1 text-start">
         <span
          className={cn('block truncate text-sm', selected ? 'font-semibold text-foreground' : 'font-medium text-foreground')}
          dir={option.code === 'ar' ? 'rtl' : 'ltr'}
         >
          {option.label}
         </span>
         <span className="block truncate text-[11px] text-muted-foreground" dir="ltr">
          {option.hint}
         </span>
        </span>
        {selected ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden /> : null}
       </div>
      </DropdownMenuItem>
     )
    })}
   </DropdownMenuContent>
  </DropdownMenu>
 )
}
